import { decodeFunctionData, parseAbi, toFunctionSelector } from 'viem'

import { AccountOp } from '../../../accountOp/accountOp'
import { HumanizerCallModule, IrCall } from '../../interfaces'
import {
  getAction,
  getAddressVisualization,
  getLabel,
  getToken,
  HexIrCall,
  isHexCall
} from '../../utils'

const setAllowanceAbi = parseAbi([
  'function setAllowance(address delegate, address token, uint96 allowanceAmount, uint16 resetTimeMin, uint32 resetBaseMin)'
])
const deleteAllowanceAbi = parseAbi(['function deleteAllowance(address delegate, address token)'])
const executeAllowanceTransferAbi = parseAbi([
  'function executeAllowanceTransfer(address safe, address token, address to, uint96 amount, address paymentToken, uint96 payment, address delegate, bytes memory signature)'
])
const addDelegateAbi = parseAbi(['function addDelegate(address delegate)'])
const removeDelegateAbi = parseAbi([
  'function removeDelegate(address delegate, bool removeAllowances)'
])

export const getAllowanceResetText = (resetTimeMin: bigint): string => {
  if (resetTimeMin === 0n) return 'No reset'
  if (resetTimeMin === 1440n) return 'Daily'
  if (resetTimeMin === 10080n) return 'Weekly'
  if (resetTimeMin === 20160n) return 'Biweekly'
  if (resetTimeMin === 43200n) return 'Monthly'
  return `Every ${resetTimeMin.toString()} minutes`
}

const setAllowanceSelector = toFunctionSelector(setAllowanceAbi[0])
const addDelegateSelector = toFunctionSelector(addDelegateAbi[0])
const removeDelegateSelector = toFunctionSelector(removeDelegateAbi[0])
const deleteAllowanceSelector = toFunctionSelector(deleteAllowanceAbi[0])
const executeAllowanceTransferSelector = toFunctionSelector(executeAllowanceTransferAbi[0])

export const getSetAllowanceResetText = (call: IrCall): string | null => {
  if (!isHexCall(call)) return null
  if (call.data.slice(0, 10) !== setAllowanceSelector) return null

  const { args } = decodeFunctionData({ abi: setAllowanceAbi, data: call.data })
  const [, , , resetTimeMin] = args

  return getAllowanceResetText(BigInt(resetTimeMin))
}

const AllowanceModule: HumanizerCallModule = (accOp: AccountOp, call: IrCall): IrCall => {
  const matcher: Record<string, (call: HexIrCall) => IrCall | undefined> = {
    [setAllowanceSelector]: (call) => {
      const { args } = decodeFunctionData({ abi: setAllowanceAbi, data: call.data })
      const [delegate, token, allowanceAmount, resetTimeMin] = args

      const fullVisualization = [
        getAction('Allow'),
        getAddressVisualization(delegate),
        getLabel('to spend'),
        getToken(token, allowanceAmount),
        getLabel(getAllowanceResetText(BigInt(resetTimeMin)))
      ]

      return { ...call, fullVisualization }
    },
    [addDelegateSelector]: (call) => {
      const { args } = decodeFunctionData({ abi: addDelegateAbi, data: call.data })
      const [delegate] = args

      const fullVisualization = [getAction('Add delegate'), getAddressVisualization(delegate)]

      return { ...call, fullVisualization }
    },
    [removeDelegateSelector]: (call) => {
      const { args } = decodeFunctionData({ abi: removeDelegateAbi, data: call.data })
      const [delegate, removeAllowances] = args

      const fullVisualization = [getAction('Remove delegate'), getAddressVisualization(delegate)]
      if (removeAllowances) fullVisualization.push(getLabel('and set allowance to 0'))

      return { ...call, fullVisualization }
    },
    [deleteAllowanceSelector]: (call) => {
      const { args } = decodeFunctionData({ abi: deleteAllowanceAbi, data: call.data })
      const [delegate, token] = args

      const fullVisualization = [
        getAction('Remove allowance for'),
        getAddressVisualization(delegate),
        getToken(token, 0n)
      ]

      return { ...call, fullVisualization }
    },
    [executeAllowanceTransferSelector]: (call) => {
      const { args } = decodeFunctionData({
        abi: executeAllowanceTransferAbi,
        data: call.data
      })
      const [, token, , amount, , , delegate] = args

      const fullVisualization = [
        getAction('Execute allowance for'),
        getAddressVisualization(delegate),
        getLabel('for'),
        getToken(token, amount)
      ]

      return { ...call, fullVisualization }
    }
  }
  if (call.fullVisualization || !isHexCall(call)) return call
  const match = matcher[call.data.slice(0, 10)]
  if (!match) return call
  const newCall = match(call)
  if (!newCall) return call
  return newCall
}

export default AllowanceModule
